import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Seo, breadcrumbJsonLd, serviceJsonLd, faqJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { LeadForm } from '@/components/LeadForm';
import { SERVICES, SITE } from '@/data/site-config';
import { servicesContent } from '@/data/services-content';

export default function ServicePage() {
  const { slug } = useParams();
  const content = servicesContent[slug];
  const meta = SERVICES.find((s) => s.slug === slug);
  if (!content || !meta) return <Navigate to="/services" replace />;

  const path = `/services/${slug}`;
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: meta.title, path },
  ];

  return (
    <>
      <Seo
        title={`${meta.title} in ${SITE.city}, ${SITE.stateAbbr}`}
        description={content.metaDescription}
        canonical={path}
        jsonLd={[breadcrumbJsonLd(breadcrumbs), serviceJsonLd(meta.title, content.metaDescription, slug), faqJsonLd(content.faqs)]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <section className="container-x py-10">
        <span className="eyebrow">{content.eyebrow}</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold mt-3">{content.h1}</h1>
        <p className="mt-5 text-[color:var(--ft-muted)] text-lg max-w-3xl">{content.intro}</p>
      </section>

      <section className="container-x grid gap-8 lg:grid-cols-12 items-start">
        <div className="lg:col-span-7 prose-dark">
          <h2>Who it’s for</h2>
          <ul>{content.whoFor.map((it, i) => (<li key={i}>{it}</li>))}</ul>
          <h2>What we work on</h2>
          <ul>{content.workOn.map((it, i) => (<li key={i}>{it}</li>))}</ul>
          <h2>What’s included</h2>
          <ul>{content.included.map((it, i) => (<li key={i}>{it}</li>))}</ul>
          <h2>What to expect</h2>
          <p>{content.whatToExpect}</p>
          <h2>Why this approach is personal</h2>
          <p>{content.whyPersonal}</p>
          {content.safety && (<>
            <h2>Important note</h2>
            <p>{content.safety}</p>
          </>)}
        </div>
        <aside className="lg:col-span-5 lg:sticky lg:top-24">
          <LeadForm source={path} testIdPrefix={`svc-${slug}`} />
        </aside>
      </section>

      <section className="container-x py-12">
        <h2 className="font-display text-2xl font-bold mb-4">Frequently asked</h2>
        <div className="grid gap-2 max-w-3xl">
          {content.faqs.map((f, i) => (
            <details key={i} className="surface p-5"><summary className="font-semibold cursor-pointer">{f.q}</summary><p className="mt-3 text-[color:var(--ft-muted)] leading-relaxed">{f.a}</p></details>
          ))}
        </div>
      </section>

      <section className="container-x py-8">
        <h2 className="font-display text-xl font-semibold mb-3">Related services</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.filter((s) => s.slug !== slug).slice(0, 3).map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="surface card-hover p-5"><h3 className="font-display font-semibold mb-1">{s.title}</h3><p className="text-sm text-[color:var(--ft-muted)]">{s.short}</p><span className="mt-3 inline-flex items-center gap-1 text-sm text-accent">View <ArrowRight size={14} /></span></Link>
          ))}
        </div>
      </section>
    </>
  );
}
